import { useEffect, useState } from "react";
import { useFeedbackReview } from "../hooks/useFeedbackReview";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import {
  PLAYER_RESPONSE_SIGNALS,
  PLAYER_RESPONSE_SIGNAL_CONFIG,
  type PlayerResponseSignal,
} from "../responseSignals";

/**
 * Three-chip player response affordance — PR 6 of the Feedback
 * Player UX redesign (Mockup Phase 6).
 *
 * Mounted on:
 *   - the unified Review card (under the meta line),
 *   - the focused player modal (between the body and the Mark
 *     reviewed footer).
 *
 * Persists by calling `markReviewed` (the existing
 * `POST /api/my-feedback/review` endpoint) with the structured
 * signal encoded into the `reflection` free-text field — see
 * `responseSignals.ts` for the encoding contract. No backend
 * migration needed for PR 6; a future column promotion can strip
 * the prefix on persist without touching this surface.
 *
 * State transitions:
 *   - "default" (no current signal) — three chips + an optional
 *     text field collapsed.
 *   - "responded" — collapses to a single "You · ⚡ I tried this"
 *     summary chip + optional note + "Edit response" button.
 */

export type PlayerResponseTarget =
  | { kind: "note"; noteId: number }
  | { kind: "playlist"; playlistId: number }
  /** PR 7: clips now persist via the same endpoint once the
   * backend gained `clip_id` support on `coaching_reviews`. */
  | { kind: "clip"; clipId: number };

export type PlayerResponseChipsProps = {
  target: PlayerResponseTarget;
  /** Current signal from the bundle, or `null` if none. */
  currentSignal: PlayerResponseSignal | null;
  /** Current note text from the bundle (empty when none). */
  currentNote: string;
  /** Optional callback so the caller can react to a successful
   * update (e.g. close the modal, refresh local state). */
  onResponded?: () => void;
};

export function PlayerResponseChips({
  target,
  currentSignal,
  currentNote,
  onResponded,
}: PlayerResponseChipsProps) {
  const review = useFeedbackReview();
  const toast = useToast();

  const [editing, setEditing] = useState(!currentSignal);
  const [selected, setSelected] = useState<PlayerResponseSignal | null>(
    currentSignal,
  );
  const [note, setNote] = useState(currentNote);
  const [noteOpen, setNoteOpen] = useState(false);

  // Re-sync from props when the surrounding bundle refreshes (e.g.
  // after a different surface persisted a response for the same id).
  useEffect(() => {
    setSelected(currentSignal);
    setNote(currentNote);
    setEditing(!currentSignal);
    setNoteOpen(!!currentNote);
  }, [currentSignal, currentNote, target.kind, idFor(target)]);

  async function submit(signal: PlayerResponseSignal) {
    setSelected(signal);
    try {
      const targetIds =
        target.kind === "note"
          ? { note_id: target.noteId }
          : target.kind === "playlist"
            ? { playlist_id: target.playlistId }
            : { clip_id: target.clipId };
      // Phase B: send the signal as its own field + the note as a
      // prefix-free reflection (no more `[SIGNAL:*]` encoding).
      await review.markReviewed({
        ...targetIds,
        response_signal: signal,
        reflection: note.trim(),
      });
      toast.success("Sent to your coach.");
      setEditing(false);
      onResponded?.();
    } catch (err) {
      toast.error(errorMessage(err) || "Couldn't send your response.");
    }
  }

  // The "edit response" entry from the responded state — open the
  // full chip + note editor again, pre-populated.
  function startEditing() {
    setEditing(true);
    setNoteOpen(!!currentNote);
  }

  if (!editing && currentSignal) {
    const config = PLAYER_RESPONSE_SIGNAL_CONFIG[currentSignal];
    return (
      <div
        className="flex flex-col gap-2 rounded-panel border border-accent/40 bg-accent/5 px-3 py-2"
        data-testid="player-response-chips"
        data-response-state="responded"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span
            className="text-xs font-display text-ink"
            data-testid="player-response-summary"
          >
            <span aria-hidden="true">{config.glyph}</span>{" "}
            <span>You said: {config.label}</span>
          </span>
          <button
            type="button"
            onClick={startEditing}
            data-testid="player-response-edit"
            className="eyebrow rounded-control text-[0.7rem] text-ink-muted transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            Edit response
          </button>
        </div>
        {currentNote ? (
          <p
            className="text-xs text-ink-muted"
            data-testid="player-response-note"
          >
            "{currentNote}"
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-panel border border-rule bg-surface px-3 py-2 shadow-rest"
      data-testid="player-response-chips"
      data-response-state={selected ? "selecting" : "default"}
    >
      <p className="eyebrow text-[0.6rem]">How'd it land?</p>
      <div
        className="flex flex-wrap gap-1.5"
        role="group"
        aria-label="Response signals"
      >
        {PLAYER_RESPONSE_SIGNALS.map((signal) => {
          const config = PLAYER_RESPONSE_SIGNAL_CONFIG[signal];
          const isActive = selected === signal;
          const isPending = review.isPending && isActive;
          return (
            <button
              key={signal}
              type="button"
              onClick={() => submit(signal)}
              disabled={review.isPending}
              aria-pressed={isActive}
              data-feedback-response-chip={signal}
              data-active={isActive}
              className={[
                "inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-xs font-display transition-colors disabled:opacity-50",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas",
                signal === "got_it"
                  ? isActive
                    ? "bg-good text-navy"
                    : "bg-good/10 text-good hover:bg-good/20"
                  : signal === "need_help"
                    ? isActive
                      ? "bg-flag text-navy"
                      : "bg-flag/10 text-flag hover:bg-flag/20"
                    : isActive
                      ? "bg-accent text-accent-ink"
                      : "bg-accent/10 text-ink hover:bg-accent/20",
              ].join(" ")}
            >
              <span aria-hidden="true">{config.glyph}</span>
              <span>{isPending ? "…" : config.label}</span>
            </button>
          );
        })}
      </div>
      {/* Controlled disclosure — avoids the native <details>/<summary>
       * triangle marker (we don't render any system chrome). The
       * caret + label is a single button toggling the textarea. */}
      <button
        type="button"
        onClick={() => setNoteOpen((open) => !open)}
        aria-expanded={noteOpen}
        data-testid="player-response-note-toggle"
        className="eyebrow self-start rounded-control text-[0.7rem] text-ink-muted transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
      >
        {noteOpen ? "Hide note" : "Add a note (optional)"}
      </button>
      {noteOpen ? (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a quick note for your coach…"
          maxLength={1000}
          rows={2}
          data-testid="player-response-note-input"
          className="w-full rounded-control border border-rule bg-surface px-2 py-1 text-sm text-ink resize-y focus:outline-none focus:ring-2 focus:ring-accent"
        />
      ) : null}
    </div>
  );
}

function idFor(t: PlayerResponseTarget): number {
  if (t.kind === "note") return t.noteId;
  if (t.kind === "playlist") return t.playlistId;
  return t.clipId;
}
